import { useState } from 'react';
import { Camera } from 'lucide-react';

// Known third-party image hosts whose content we must never display.
const BLOCKED_HOSTS = [
  'pokemontcg.io',
  'scryfall.io',
  'scryfall.com',
  'ygoprodeck.com',
  'images.cardmarket',
];

function isThirdPartyUrl(src: string | null | undefined): boolean {
  if (!src) return false;
  return BLOCKED_HOSTS.some((host) => src.includes(host));
}

// Game-specific gradient colors for placeholder cards.
// Keys cover both the short form stored by card-fetch.service.ts ('Magic', 'YuGiOh')
// and the long display forms that may appear in older card records or UI labels.
const GAME_COLORS: Record<string, { from: string; to: string }> = {
  // Pokémon
  'Pokemon':              { from: '#f59e0b', to: '#b45309' },
  // Magic: The Gathering — stored as 'Magic' by the backend
  'Magic':                { from: '#7c3aed', to: '#3b0764' },
  'Magic: The Gathering': { from: '#7c3aed', to: '#3b0764' },
  // Yu-Gi-Oh — stored as 'YuGiOh' by the backend
  'YuGiOh':               { from: '#dc2626', to: '#7f1d1d' },
  'Yu-Gi-Oh!':            { from: '#dc2626', to: '#7f1d1d' },
  // Other TCGs
  'One Piece':            { from: '#f97316', to: '#9a3412' },
  'Dragon Ball':          { from: '#f97316', to: '#ca8a04' },
  'Dragon Ball Super':    { from: '#f97316', to: '#ca8a04' },
  'Digimon':              { from: '#3b82f6', to: '#1e3a8a' },
  // Sports
  'Baseball':             { from: '#2563eb', to: '#1e3a8a' },
  'Basketball':           { from: '#ea580c', to: '#7c2d12' },
  'Football':             { from: '#16a34a', to: '#14532d' },
  'Soccer':               { from: '#22c55e', to: '#166534' },
  'Hockey':               { from: '#475569', to: '#0f172a' },
};
const DEFAULT_COLORS = { from: '#6b7280', to: '#1f2937' };

interface CardImageProps {
  src?: string | null;
  alt?: string;
  className?: string;
  /** Show a smaller placeholder (for thumbnails) */
  compact?: boolean;
  /** Card name displayed on the placeholder */
  cardName?: string;
  /** Card number displayed on the placeholder (e.g. "025/102") */
  cardNumber?: string;
  /** Game name used for placeholder gradient color */
  game?: string;
}

/**
 * Renders a user-uploaded card photo, or a styled placeholder showing the
 * card name and number.
 *
 * LEGAL NOTE: This component intentionally blocks third-party API image URLs
 * (pokemontcg.io, Scryfall, ygoprodeck, etc.) — those images are copyrighted
 * by Pokémon, WotC, Konami, etc. Only user-uploaded images stored on our own
 * servers are ever displayed.
 */
export default function CardImage({
  src,
  alt = 'Card',
  className = '',
  compact = false,
  cardName,
  cardNumber,
  game,
}: CardImageProps) {
  const [errored, setErrored] = useState(false);

  if (isThirdPartyUrl(src)) {
    console.error('[CardImage] Blocked third-party API image URL:', src);
  }

  const showPlaceholder = !src || errored || isThirdPartyUrl(src);

  if (showPlaceholder) {
    const name    = cardName ?? alt;
    const colors  = (game ? GAME_COLORS[game] : null) ?? DEFAULT_COLORS;
    const gradient = `linear-gradient(150deg, ${colors.from}, ${colors.to})`;

    if (compact) {
      return (
        <div
          className={`flex items-center justify-center overflow-hidden ${className}`}
          style={{ background: gradient }}
        >
          <span className="text-white font-bold text-xs text-center px-0.5 leading-tight line-clamp-2 drop-shadow">
            {name}
          </span>
        </div>
      );
    }

    return (
      <div
        className={`relative flex flex-col items-center justify-between overflow-hidden ${className}`}
        style={{ background: gradient, padding: '6% 5%' }}
      >
        {/* Game label */}
        {game && (
          <p className="self-start text-white/60 text-xs font-medium truncate w-full leading-none mb-1">
            {game}
          </p>
        )}

        {/* Card name — centered, takes up the bulk of the space */}
        <div className="flex-1 flex items-center justify-center w-full">
          <p className="text-white font-bold text-center leading-tight drop-shadow-sm"
             style={{ fontSize: 'clamp(0.5rem, 3cqw, 0.875rem)', wordBreak: 'break-word' }}>
            {name}
          </p>
        </div>

        {/* Card number + upload hint */}
        <div className="w-full flex items-end justify-between mt-1">
          <span className="text-white/60 text-xs leading-none">
            {cardNumber ? `#${cardNumber}` : ''}
          </span>
          <Camera className="text-white/40 flex-shrink-0" style={{ width: '14%', height: 'auto' }} strokeWidth={1.5} />
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover ${className}`}
      onError={() => setErrored(true)}
      loading="lazy"
    />
  );
}
